/**
 * Leave API client. Wraps /v1/leave/* and /v1/me/leave/* endpoints.
 * PR 4b — surfaces the leave workflow that PR 4a's substrate enables.
 */

import type {
  ApproveLeaveRequestInput,
  BackdatedLeaveRequestInput,
  BalanceCorrectionInput,
  CancelLeaveRequestInput,
  LeaveBalanceResponse,
  LeaveDaysPreviewInput,
  LeaveDaysPreviewResponse,
  LeaveRequestListResponse,
  LeaveRequestResponse,
  LeaveTransactionListResponse,
  ListLeaveRequestsQuery,
  RejectLeaveRequestInput,
  SubmitLeaveRequestInput,
} from '@kora/types';
import { apiRequest } from './client.js';

export function previewLeaveDays(
  body: LeaveDaysPreviewInput,
): Promise<LeaveDaysPreviewResponse> {
  return apiRequest<LeaveDaysPreviewResponse>(
    '/v1/leave/requests/preview-days',
    { method: 'POST', body },
  );
}

export function submitLeaveRequest(
  body: SubmitLeaveRequestInput,
): Promise<LeaveRequestResponse> {
  return apiRequest<LeaveRequestResponse>('/v1/leave/requests', {
    method: 'POST',
    body,
  });
}

export function listLeaveRequests(
  q: Partial<ListLeaveRequestsQuery> = {},
): Promise<LeaveRequestListResponse> {
  const params = new URLSearchParams();
  if (q.view) params.set('view', q.view);
  if (q.status) params.set('status', q.status);
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  const query = params.toString();
  return apiRequest<LeaveRequestListResponse>(
    `/v1/leave/requests${query ? `?${query}` : ''}`,
  );
}

export function getLeaveRequest(id: string): Promise<LeaveRequestResponse> {
  return apiRequest<LeaveRequestResponse>(
    `/v1/leave/requests/${encodeURIComponent(id)}`,
  );
}

export function approveLeaveRequest(
  id: string,
  body: ApproveLeaveRequestInput = {},
): Promise<unknown> {
  return apiRequest(
    `/v1/leave/requests/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body },
  );
}

export function rejectLeaveRequest(
  id: string,
  body: RejectLeaveRequestInput,
): Promise<unknown> {
  return apiRequest(
    `/v1/leave/requests/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body },
  );
}

export function cancelLeaveRequest(
  id: string,
  body: CancelLeaveRequestInput = {},
): Promise<LeaveRequestResponse> {
  return apiRequest<LeaveRequestResponse>(
    `/v1/leave/requests/${encodeURIComponent(id)}/cancel`,
    { method: 'POST', body },
  );
}

export function getMyLeaveBalance(): Promise<LeaveBalanceResponse> {
  return apiRequest<LeaveBalanceResponse>('/v1/me/leave/balance');
}

export function listMyLeaveTransactions(): Promise<LeaveTransactionListResponse> {
  return apiRequest<LeaveTransactionListResponse>('/v1/me/leave/transactions');
}

export function postBalanceCorrection(
  body: BalanceCorrectionInput,
): Promise<{ id: string }> {
  return apiRequest<{ id: string }>('/v1/admin/leave/corrections', {
    method: 'POST',
    body,
  });
}

export function postBackdatedLeave(
  body: BackdatedLeaveRequestInput,
): Promise<{ id: string; workingDaysCount: number }> {
  return apiRequest<{ id: string; workingDaysCount: number }>(
    '/v1/leave/requests/backdated',
    { method: 'POST', body },
  );
}
